// Indica se há operação em andamento que não pode ser interrompida por um recarregamento:
//  - requisições de gravação em curso (POST/PUT/PATCH/DELETE), via trackBusyRequests();
//  - formulários com dados digitados e ainda não enviados (detectados automaticamente);
//  - diálogos do SweetAlert2 abertos;
//  - operações marcadas manualmente com beginBusy().
// A atualização automática do PWA consulta este estado para nunca recarregar a página
// no meio de um lançamento.

let count = 0;
const listeners = new Set();
const dirtyRoots = new Set();

const notify = () => {
  const busy = isBusy();
  listeners.forEach((listener) => listener(busy));
};

export function beginBusy() {
  count += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    count -= 1;
    notify();
  };
}

function pruneDirtyRoots() {
  dirtyRoots.forEach((root) => {
    if (!root.isConnected) dirtyRoots.delete(root);
  });
}

function swalOpen() {
  return Boolean(document.querySelector('.swal2-container'));
}

export function isBusy() {
  if (typeof document === 'undefined') return count > 0;
  pruneDirtyRoots();
  return count > 0 || dirtyRoots.size > 0 || swalOpen();
}

/** Gravações em curso: nunca recarregar, nem a pedido do usuário. */
export function hasPendingWrites() {
  return count > 0;
}

export function onBusyChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Formulário/modal que contém o campo editado. Ao ser enviado ou fechado (removido da
// página), deixa de contar como operação em andamento.
const ROOT_SELECTOR = 'form, [role="dialog"], .modal, .sp-modal, .modal-overlay';

let formTrackingStarted = false;

export function startFormTracking() {
  if (formTrackingStarted || typeof document === 'undefined') return;
  formTrackingStarted = true;

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('.swal2-container')) return;
    const root = target.closest(ROOT_SELECTOR);
    if (!root || dirtyRoots.has(root)) return;
    dirtyRoots.add(root);
    notify();
  }, true);

  const release = (event) => {
    if (dirtyRoots.delete(event.target)) notify();
  };
  document.addEventListener('submit', release, true);
  document.addEventListener('reset', release, true);

  // Fechamento de modais remove o elemento da página: reavalia o estado
  new MutationObserver(() => {
    if (dirtyRoots.size === 0) return;
    const before = dirtyRoots.size;
    pruneDirtyRoots();
    if (dirtyRoots.size !== before) notify();
  }).observe(document.body, { childList: true, subtree: true });
}

const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

/** Marca como ocupado enquanto houver requisição de gravação em curso no cliente axios. */
export function trackBusyRequests(axiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    if (WRITE_METHODS.includes((config.method || 'get').toLowerCase())) {
      config.__releaseBusy = beginBusy();
    }
    return config;
  });
  axiosInstance.interceptors.response.use(
    (response) => {
      response.config?.__releaseBusy?.();
      return response;
    },
    (error) => {
      error.config?.__releaseBusy?.();
      return Promise.reject(error);
    },
  );
}
